/**
 * Page root for the transport wizard.
 *
 * Renders the provider, the icon sprite, its own page header (title,
 * connection status, EN/DE toggle), the stepper, and the scene router. Uses
 * the global app sidebar, which App.jsx renders around this page.
 */

import React, { useEffect } from "react";
import { TransportV2Provider, useTransportV2 } from "./context/TransportV2Context";
import IconSprite from "./components/IconSprite";
import Stepper from "./components/Stepper";
import Scene0Space from "./scenes/Scene0Space";
import Scene1Landscape from "./scenes/Scene1Landscape";
import Scene2Package from "./scenes/Scene2Package";
import Scene3Artifacts from "./scenes/Scene3Artifacts";
import Scene4Config from "./scenes/Scene4Config";
import Scene5Review from "./scenes/Scene5Review";
import { useLang } from "./i18n";
import "./styles/transport-v2.css";

function SceneRouter({ user }) {
  const { state } = useTransportV2();
  switch (state.step) {
    case 0: return <Scene0Space />;
    case 1: return <Scene1Landscape />;
    case 2: return <Scene2Package />;
    case 3: return <Scene3Artifacts />;
    case 4: return <Scene4Config />;
    case 5: return <Scene5Review user={user} />;
    default: return <Scene0Space />;
  }
}

function StepperGate({ onJump }) {
  const { state, dispatch } = useTransportV2();
  return (
    <Stepper
      active={state.step}
      onJump={(i) => {
        if (i < state.step) dispatch({ type: "SET_STEP", payload: i });
      }}
    />
  );
}

function InnerV2({ user }) {
  const { state } = useTransportV2();
  const { lang, setLang, t } = useLang();
  // Do not change the browser tab title/subtitle here, App.jsx already does that.
  useEffect(() => { /* no-op */ }, [state.step]);
  return (
    <div className="v2main">
      <header className="hdr">
        <div>
          <h1>{t("hdr.title")}</h1>
          <div className="sub">{t("hdr.sub")}</div>
        </div>
        <div className="hdr-right">
          <span className="hdr-conn"><i className="dot" /><span>{t("hdr.conn")}</span></span>
          <button
            className="hbtn hbtn--lang"
            title={lang === "de" ? "Switch to English" : "Auf Deutsch umschalten"}
            onClick={() => setLang(lang === "de" ? "en" : "de")}
          >
            {lang === "de" ? "EN" : "DE"}
          </button>
        </div>
      </header>
      <StepperGate />
      <div className="content">
        <SceneRouter user={user} />
      </div>
    </div>
  );
}

export default function TransportV2Page({ spaces, user }) {
  // The spaces prop is accepted for consistency with how other pages receive
  // it, but V2 loads its own instances via loadAllInstances (spaces does not
  // contain instances).
  void spaces;
  return (
    <div className="iio iio-transport-v2">
      <IconSprite />
      <TransportV2Provider>
        <InnerV2 user={user} />
      </TransportV2Provider>
    </div>
  );
}
